import React, { useEffect, useState } from "react";
import styled from "styled-components";
import './i18n';
import Header from "./Header";
import Footer from "./Footer";
import { useTranslation } from 'react-i18next';
import { jwtDecode } from "jwt-decode";
import EUCA from "./images/EUCALY2.jpg"

const Profile = () => {
  const [account, setAccount] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState("");
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [consultations, setConsultations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [confirmingId, setConfirmingId] = useState(null);
  const [consultationError, setConsultationError] = useState("");
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
  const { i18n, t } = useTranslation();

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/";

  const token = localStorage.getItem("token");
  let userId = null;

  try {
    const decoded = token ? jwtDecode(token) : null;
    userId = decoded?.userId;
  } catch (error) {
    console.error("Invalid token:", error);
  }

  useEffect(() => {
  const fetchAccount = async () => {
    try {
      if (!userId) {
        console.warn("No userId, skipping fetch");
        return;
      }
      const response = await fetch(`${API_URL}account/${userId}`);
      if (!response.ok) throw new Error(`Fetch failed with status ${response.status}`);
      const data = await response.json();
      setAccount(data);
    } catch (err) {
      console.error("Error loading account:", err);
    }
  };

  fetchAccount();
}, [API_URL, userId]);

  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const response = await fetch(`${API_URL}reps`);
        const data = await response.json();
        setCoaches(data);
      } catch (err) {
        console.error("Error loading coaches:", err);
      }
    };

    fetchCoaches();
  }, [API_URL]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (!userId) return;
        const response = await fetch(`${API_URL}orders/user/${userId}`);
        const data = await response.json();
        setOrders(data);
      } catch (err) {
        console.error("Error fetching orders:", err);
      }
    };
  
    fetchOrders();
  }, [API_URL, userId]);

  const handleCoachChange = (e) => {
    const coachId = e.target.value;
    setSelectedCoach(coachId);
    setConfirmationVisible(true);
  };

  const handleCoachAssignment = async () => {
  if (!selectedCoach) return;

  try {
    const response = await fetch(`${API_URL}account/${userId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ coach: selectedCoach }),
    });

    if (response.ok) {
      setAccount({ ...account, coachId: selectedCoach });
      setConfirmationVisible(false);
      window.location.reload();
    }
  } catch (err) {
    console.error("Error assigning coach:", err);
  }
};

const handleDeleteAccount = async () => {
  if (!deleteConfirmationVisible) {
    setDeleteConfirmationVisible(true);
    return;
  }

  try {
    const response = await fetch(`${API_URL}account/${account._id}`, {
      method: "DELETE",
    });

    if (response.ok) {
      localStorage.clear()
      window.location.href = "/";
    } 
  } catch (err) {
    console.error("Delete failed:", err);
  }
};

useEffect(() => {
  const fetchConsultations = async () => {
    if (!account?.email) return;
    try {
      const response = await fetch(`${API_URL}consultations/${encodeURIComponent(account.email)}`);
      
      if (response.status === 404) {
        // No consultations found, but that's not an error for us!
        setConsultations([]);
        return;
      }

      if (!response.ok) throw new Error("Failed to fetch consultations.");
      
      const data = await response.json();
      setConsultations(data);
    } catch (err) {
      console.error("Error fetching consultations:", err);
      setConsultationError("Could not load consultation history. Please try again later.");
    }
  };

  fetchConsultations();
}, [API_URL, userId, account]);

  const convertConsultationToAvailability = async (coachLN, date, time, duration) => {
  try {
    const formattedDate = new Date(date).toISOString().split('T')[0];

    const response = await fetch(`${API_URL}convertConsultationToAvailability`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ coachLN, date: formattedDate, time, duration }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Something went wrong!');
    }

    console.log("Success:", data.message);
    return data;
  } catch (error) {
    console.error('Error converting consultation to availability:', error);
  }
};

 return (
    <FULLpage>
      <Header />
      <ProfileContainer>
        <H2>{t("profile.title")}</H2>
        {account ? (
          <ProfileContent>
            <LeftPanel>
              <ProfileCard>
                <InfoBlock>
                  <Label>{t("profile.Fullname")}</Label>
                  <Value>
                    {account.firstName} {account.lastName}
                  </Value>
                </InfoBlock>

                <Sidebyside>
                  <InfoBlock>
                    <Label>Email</Label>
                    <Value>{account.email}</Value>
                  </InfoBlock>
                  <InfoBlock>
                    <Label>{t("profile.Number")}</Label>
                    <Value>{account.phoneNumber}</Value>
                  </InfoBlock>
                </Sidebyside>

                {account.assignedCoach ? (
                  <InfoBlock>
                    <Label>{t("profile.coach")}</Label>
                    <Value>{account.assignedCoach}</Value>
                  </InfoBlock>
                ) : (
                  <InfoBlock>
                    <Label>{t("profile.coachAssigner")}</Label>
                    <Select value={selectedCoach || ""} onChange={handleCoachChange}>
                      <option value="">{t("profile.coachAssigned")}</option>
                      {coaches.map((coach) => (
                        <option key={coach._id} value={coach._id}>
                          {coach.firstName} {coach.lastName}
                        </option>
                      ))}
                    </Select>
                    {confirmationVisible && (
                      <Confirmation>
                        <Warning>{t("profile.finalChoice")}</Warning>
                        <button onClick={handleCoachAssignment}>
                          {t("profile.confirmCoach")}
                        </button>
                      </Confirmation>
                    )}
                  </InfoBlock>
                )}

                <InfoBlock>
  <Label>{t("profile.deleteAccount")}</Label>
  <div>
    {deleteConfirmationVisible ? (
      <div>
        <p>{t("profile.confirmDeleteMessage")}</p>
        <ButtonCon onClick={() => { handleDeleteAccount(); setDeleteConfirmationVisible(false); }}>
        ✅ {t("profile.confirmDelete")}
        </ButtonCon>
        <ButtonCan onClick={() => setDeleteConfirmationVisible(false)}>
          ❌ {t("profile.cancelAcc")}
        </ButtonCan>
      </div>
    ) : (
      <Button onClick={() => setDeleteConfirmationVisible(true)}>
        {t("profile.deleteAccount")}
      </Button>
    )}
  </div>
</InfoBlock>
              </ProfileCard>
            </LeftPanel>

            <RightPanel>
              <OrderHistory>
                <h3>{t("profile.Purchase")}</h3>
                {orders.length === 0 ? (
                  <p>{t("profile.noOrders")}</p>
                ) : (
                  orders.map((order, index) => (
                    <OrderAndHistoryDivs key={order._id || index}>
                      <p>
                        <strong>{t("profile.Date&Time")}:</strong>{" "}
                        {new Date(order.createdAt).toLocaleDateString()}
                      </p>
                      <p>
                      <strong>Total:</strong> {order.totalPaid != null ? `$${order.totalPaid.toFixed(2)}` : "N/A"}
                      </p>
                      <p>
                        <strong>Coach:</strong>{" "}
                        {order.coachName || "No coach selected at checkout/on profile."}
                      </p>
                      <ItemList>
                        {order.products.map((item, idx) => (
                          <li key={idx}>
                            {item.name?.[i18n.language] || "Product Name Missing"} (x
                            {item.quantity})
                          </li>
                        ))}
                      </ItemList>
                    </OrderAndHistoryDivs>
                  ))
                )}
              </OrderHistory>

              <ConsultationHistory>
                <h3>{t("profile.Consult")}</h3>
                {successMessage && (
                  <p style={{ color: "green", fontWeight: "bold" }}>{successMessage}</p>
                )}
                {consultationError && <p>{consultationError}</p>}
                {!consultationError && consultations.length === 0 && (
                  <p>{t("profile.noConsult")}</p>
                )}
                {consultations.map((c) => {
                  const dt = new Date(`${c.date}T${c.time}`);
                  const isConfirming = confirmingId === c._id;

                  return (
                    <OrderAndHistoryDivs key={c._id}>
                      <p>
                        <strong>Type:</strong>{" "}
                        {c.consultationType?.[i18n.language] || "—"}
                      </p>
                      <p>
                        <strong>Duration:</strong> {c.displayDuration ?? "—"} minutes
                      </p>
                      <p>
                        <strong>{t("profile.Date&Time")}:</strong>{" "}
                        {dt.toLocaleDateString()} at{" "}
                        {dt.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                      <p>
                        <strong>Coach:</strong> {c.coachLN}
                      </p>
                      {!isConfirming ? (
                        <Button onClick={() => setConfirmingId(c._id)}>
                          {t("profile.cancel")}
                        </Button>
                      ) : (
                        <div>
                          <ButtonCon2
                            onClick={() => {
                              convertConsultationToAvailability(
                                c.coachLN,
                                c.date,
                                c.time,
                                c.realDuration
                              );
                              setConfirmingId(null);
                              setSuccessMessage(t("profile.noBtn"));
                              setTimeout(() => {
                                window.location.reload();
                              }, 2000);
                            }}
                          >
                            ✅ {t("profile.yesBtn")}
                          </ButtonCon2>
                          <ButtonCan2 onClick={() => setConfirmingId(null)}>
                            ❌ {t("profile.noBtn")}
                          </ButtonCan2>
                        </div>
                      )}
                    </OrderAndHistoryDivs>
                  );
                })}
              </ConsultationHistory>
            </RightPanel>
          </ProfileContent>
        ) : (
          <p>Loading your profile...</p>
        )}
      </ProfileContainer>
      <Footer />
    </FULLpage>
  );
};

export default Profile;

const ButtonCan = styled.button`
background-color: rgb(167, 24, 24); 
    color: white;
    font-weight: bold;
    font-size: 1rem; 
    border: none;
    padding: 10px 15px; 
    margin: 0 20px;
    border-radius: 8px;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1); 
    transition: background-color 0.3s, transform 0.2s;

    &:hover {
      background-color: rgb(255, 0, 0);
      transform: scale(1.05);
    }

    &:focus {
      outline: 3px solid rgba(100, 135, 70, 0.6);
    }
`

const ButtonCan2 = styled.button`
background-color: rgb(167, 24, 24); 
    color: white;
    font-weight: bold;
    font-size: 0.6rem; 
    border: none;
    padding: 10px 15px; 
    margin: 0 20px;
    border-radius: 8px;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1); 
    transition: background-color 0.3s, transform 0.2s;

    &:hover {
      background-color: rgb(255, 0, 0);
      transform: scale(1.05);
    }

    &:focus {
      outline: 3px solid rgba(100, 135, 70, 0.6);
    }
`

const ButtonCon = styled.button`
background-color: rgba(100, 135, 70, 1); 
    color: white;
    font-weight: bold;
    font-size: 1rem; 
    border: none;
    padding: 10px 15px; 
    margin: 0 20px;
    border-radius: 8px;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1); 
    transition: background-color 0.3s, transform 0.2s;

    &:hover {
      background-color: rgb(13, 182, 7);
      transform: scale(1.05);
    }

    &:focus {
      outline: 3px solid rgba(100, 135, 70, 0.6);
    }
`

const ButtonCon2 = styled.button`
background-color: rgba(100, 135, 70, 1); 
    color: white;
    font-weight: bold;
    font-size: 0.6rem; 
    border: none;
    padding: 10px 15px; 
    margin: 0 20px;
    border-radius: 8px;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1); 
    transition: background-color 0.3s, transform 0.2s;

    &:hover {
      background-color: rgb(13, 182, 7);
      transform: scale(1.05);
    }

    &:focus {
      outline: 3px solid rgba(100, 135, 70, 0.6);
    }
`

const Button = styled.button`
 background-color: rgba(233, 149, 129, 1); 
    color: white;
    font-weight: bold;
    font-size: 1rem; 
    border: none;
    padding: 10px 15px; 
    margin: 0 20px;
    border-radius: 8px;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1); 
    transition: background-color 0.3s, transform 0.2s;

    &:hover {
      background-color: rgb(177, 25, 14);
      transform: scale(1.05);
    }

    &:focus {
      outline: 3px solid rgba(100, 135, 70, 0.6);
    }
`

const FULLpage = styled.div`
background-image: url(${EUCA});
background-repeat: no-repeat;
background-position: center top;
background-size: 2200px auto;
`

const Sidebyside = styled.div`
display: flex;
gap: 1rem;
justify-content: center;
`

const ProfileContent = styled.div`
  gap: 1rem;
  padding: 1rem;
  
`;

const LeftPanel = styled.div`
  min-width: 280px;
 
`;

const H2 = styled.h2`
text-align: center;
`;

const RightPanel = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2rem;
`;

const ProfileContainer = styled.div`
  text-align: center;
  padding: 1.7rem 0;
  margin: 0 auto;
  min-height: 100vh;

`;

const ProfileCard = styled.div`
  background-color: rgba(133, 204, 151, 0.2);
  box-shadow: 1px 1px 1px black;
  padding: 2.5rem;
  border-radius: 1rem;
  text-align: center;
`;

const InfoBlock = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.p`
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
  color: #333;
`;

const Value = styled.p`
  margin: 0;
  color: #555;
  font-size: 1.1rem;
`;

const Warning = styled.p`
font-style: italic;
font-size: 20px;
margin: 50px 90px;
padding: 10px 2px;
box-shadow: 0 2px 20px rgba(179, 179, 179, 1);
border: 0px solid grey;
border-radius: 5px;
background-color: rgba(201, 200, 200, 0.85);
`

const Select = styled.select`
  padding: 0.8rem;
  width: 100%;
  border-radius: 0.5rem;
  border: 1px solid #ddd;
  margin-top: 0.5rem;
  font-size: 1rem;
`;

const Confirmation = styled.div`
  margin-top: 1rem;
  button {
    background-color: #4CAF50;
    color: white;
    padding: 0.7rem 1.5rem;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    &:hover {
      background-color: #45a049;
    }
  }
`;

const OrderHistory = styled.div`
  margin-top: 3rem;
  width: 50%;
  background: rgba(133, 204, 151, 0.2); 
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 6px solid rgba(133, 204, 151, 0.49);
  box-shadow: 0 3px 8px rgba(0,0,0,0.5);
  max-height: 400px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
`;

const ConsultationHistory = styled.div`
  margin-top: 3rem;
  width: 50%;
  background: rgba(133, 204, 151, 0.2); 
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 6px solid rgba(133, 204, 151, 0.49);
  box-shadow: 0 3px 8px rgba(0,0,0,0.5);
  max-height: 400px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
`;

const OrderAndHistoryDivs = styled.div`
  color: white;
  background:rgba(89,123,96,254);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 3px 8px rgba(0,0,0,0.5);
`;

const ItemList = styled.ul`
  margin-top: 1rem;
  padding-left: 1rem;
  list-style-type: disc;
  color: white;
`;
